import { Location } from '@angular/common';
import { inject, Injectable, OnDestroy } from '@angular/core';
import { Router } from '@angular/router';
import { TranslateService } from '@ngx-translate/core';
import { catchError, EMPTY, first, firstValueFrom, Observable, Subject, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { promiseWithCatch, unsubscribeAll } from 'src/app/common/app.helpers';
import ProjectLinks from 'src/app/common/project.links';
import { QueryUrlParams, RedirectUrlParams } from '../interfaces/query-url-params.interface';
import { NotificationService } from '../notifications/notifications.service';
import { Community, ListOfBasicUsers, Project } from '../swagger-api';
import { MaybeUndefined } from '../utils';
import { isRedirectUrlParams, RedirectAction } from './helpers/url-query-handler.helpers';
import { UrlQueryRequestsService } from './url-query-requests.service';

@Injectable({
  providedIn: 'root',
})
export class UrlQueryHandlerService implements OnDestroy {
  public get urlChangesTrigger(): Observable<void> {
    return this.urlChangesTrigger$.asObservable();
  }

  private urlChangesTrigger$: Subject<void> = new Subject();

  private subscriptions$: Subscription[] = [];

  private doQueryUrlStrategy: {
    [key in RedirectAction]: (params: RedirectUrlParams) => void;
  } = {
    community_accept: (params) => this.handleCommunityMembersPutRequest(params),
    community_reject: (params) => this.handleCommunityMembersPutRequest(params),
    project_accept: (params) => this.handleProjectMembersPutRequest(params),
    project_reject: (params) => this.handleProjectMembersPutRequest(params),
    project_accept_join_request: (params) => this.handleProjectMembersPutRequest(params),
    community_accept_join_request: (params) => this.handleCommunityMembersPutRequest(params),
  };

  private router = inject(Router);
  private location = inject(Location);
  private sessionService = inject(SessionService);
  private notificationService = inject(NotificationService);
  private translateService = inject(TranslateService);
  private urlQueryRequestsService = inject(UrlQueryRequestsService);

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public processUrlFragment(): void {
    this.urlChangesTrigger$.next();
  }

  public proceedUrlQueryParams(params: QueryUrlParams): void {
    const email: MaybeUndefined<string> = params.email;
    const returnUrl: MaybeUndefined<string> = params.returnUrl;
    const previousPage: MaybeUndefined<string> = params.previousPage;
    const verifyToken: MaybeUndefined<string> = params.verifyToken;

    if (verifyToken && returnUrl && email) {
      this.handleVerifyToken(verifyToken, returnUrl, email);
    } else if (this.sessionService.isSessionActive()) {
      const returnParams: RedirectUrlParams | unknown = {
        url: returnUrl,
        actorId: params.actor_id,
        entityId: params.entity_id,
        serverAction: params.server_action,
      };

      if (isRedirectUrlParams(returnParams)) {
        this.doQueryUrlStrategy[returnParams.serverAction](returnParams);
      } else if (returnUrl) {
        promiseWithCatch(this.router.navigate([returnUrl]));
      } else if (previousPage) {
        promiseWithCatch(this.router.navigate([previousPage]));
      } else if (this.location.path() === ProjectLinks.LINK_LOGIN || ProjectLinks.LINK_SECOND_AUTH_LEVEL) {
        promiseWithCatch(this.router.navigate([ProjectLinks.LINK_DEFAULT]));
      }
    }
  }

  private handleVerifyToken(verifyToken: string, returnUrl: string, email: string): void {
    if (this.sessionService.isSessionActive()) {
      this.sessionService.endSession();
    }

    promiseWithCatch(
      firstValueFrom(this.urlQueryRequestsService.verifyEmail(verifyToken, email)).then(() =>
        this.router.navigate([ProjectLinks.LINK_LOGIN], { queryParams: { returnUrl } }),
      ),
    );
  }

  private handleCommunityMembersPutRequest(params: RedirectUrlParams): void {
    this.subscriptions$.push(
      this.urlQueryRequestsService
        .communityMembersPut(params)
        .pipe(first())
        .subscribe({
          next: (url: string) => {
            this.handleQueryResponse(url);
            if (params.serverAction === 'community_accept_join_request') {
              const socialGroupReader = params.url?.includes('charity/view')
                ? this.urlQueryRequestsService.getCharityInfo.bind(this.urlQueryRequestsService)
                : this.urlQueryRequestsService.getCommunityInfo.bind(this.urlQueryRequestsService);

              this.getDataForNotification(params, socialGroupReader);
            }
          },
          error: () => {
            this.handleQueryResponse(ProjectLinks.LINK_DEFAULT);
          },
        }),
    );
  }

  private handleProjectMembersPutRequest(params: RedirectUrlParams): void {
    this.subscriptions$.push(
      this.urlQueryRequestsService
        .projectMembersPut(params)
        .pipe(first())
        .subscribe({
          next: (url: string) => {
            this.handleQueryResponse(url);

            if (params.serverAction === 'project_accept_join_request') {
              this.getDataForNotification(
                params,
                this.urlQueryRequestsService.getProjectInfo.bind(this.urlQueryRequestsService),
              );
            }
          },
          error: () => {
            this.handleQueryResponse(ProjectLinks.LINK_DEFAULT);
          },
        }),
    );
  }

  private handleQueryResponse(url: string): void {
    promiseWithCatch(this.router.navigateByUrl(url, { replaceUrl: true }));
  }

  private showSuccessfulMessage(userName: string, organizationName: string): void {
    const message = this.translateService.instant('MANAGE_CONFIRM_INVITE_LEADER_HINT', {
      userName,
      organizationName,
    }) as string;

    this.notificationService.showSuccessfulMessage(message);
  }

  private getDataForNotification(
    params: RedirectUrlParams,
    socialGroupReader: (id: string) => Observable<Community | Project>,
  ): void {
    this.urlQueryRequestsService
      .getUserInfo(params.actorId)
      .pipe(catchError(() => EMPTY))
      .subscribe((user: ListOfBasicUsers) => {
        socialGroupReader(params.entityId)
          .pipe(catchError(() => EMPTY))
          .subscribe((data: Community | Project) => {
            this.showSuccessfulMessage(user.items[0]?.displayed_name ?? '', data.title);
          });
      });
  }
}
